/* Element 5 — Klaviyo cart signals for the custom cart drawer.
 *
 * Shopify's pixel already sends "Added to Cart" (one product per event).
 * This adds what that misses:
 *   1. "Express Checkout Clicked" — fired when a shopper taps Apple Pay /
 *      Google Pay / PayPal / Shop Pay in the drawer. Carries the WHOLE cart
 *      plus a restore link, so a Klaviyo flow can chase people who opened the
 *      wallet sheet and then bailed (Shopify never sees their email).
 *   2. A cart-restore link: /?e5cart=<variant>:<qty>:<plan>,... rebuilds the
 *      cart INCLUDING subscription plans (Shopify's /cart/ permalinks drop
 *      selling plans), then opens the drawer.
 *
 * Standalone: does not touch cart-drawer.js. Free £0 gift lines are left out
 * of the restore link — the drawer's gift engine re-adds them by threshold.
 */
(function () {
  'use strict';

  var EVENT = 'Express Checkout Clicked';
  var DEDUPE_MS = 10 * 60 * 1000;
  var cart = null;

  function track(name, props) {
    var k = (window.klaviyo = window.klaviyo || []);
    try { k.push(['track', name, props]); } catch (e) {}
  }

  function restoreUrl(c) {
    var parts = c.items
      .filter(function (i) { return i.price > 0; })
      .map(function (i) {
        var plan = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan
          ? i.selling_plan_allocation.selling_plan.id : '';
        return i.variant_id + ':' + i.quantity + (plan ? ':' + plan : '');
      });
    return window.location.origin + '/?e5cart=' + encodeURIComponent(parts.join(','));
  }

  function payload(c, wallet) {
    var items = c.items.filter(function (i) { return i.price > 0; });
    return {
      $value: c.total_price / 100,
      $currency: c.currency,
      Wallet: wallet,
      ItemCount: items.reduce(function (n, i) { return n + i.quantity; }, 0),
      ItemNames: items.map(function (i) { return i.product_title; }),
      Items: items.map(function (i) {
        var sp = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
        return {
          ProductName: i.product_title,
          VariantName: i.variant_title,
          VariantID: i.variant_id,
          ProductID: i.product_id,
          Quantity: i.quantity,
          LinePrice: i.final_line_price / 100,
          ImageURL: i.image ? (i.image.indexOf('//') === 0 ? 'https:' + i.image : i.image) : null,
          URL: window.location.origin + i.url,
          Subscription: sp ? sp.name : 'One-time'
        };
      }),
      CartRestoreURL: restoreUrl(c)
    };
  }

  function loadCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { cart = c; return c; })
      .catch(function () { return cart; });
  }

  // Keep the snapshot fresh after every cart mutation, whoever makes it
  // (drawer, product-form.js, the bespoke pf- product sections).
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var p = nativeFetch.apply(this, arguments);
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () { setTimeout(loadCart, 50); }, function () {});
      }
    } catch (e) {}
    return p;
  };

  function walletFrom(path) {
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (!el || !el.getAttribute) continue;
      // Shopify names its wallet buttons by tag (shop-pay-wallet-button,
      // shopify-google-pay-button, shopify-apple-pay-button, shopify-paypal-button).
      var s = (el.tagName + ' ' + (el.getAttribute('aria-label') || '') + ' ' + (el.getAttribute('data-testid') || '')).toLowerCase();
      if (/apple/.test(s)) return 'Apple Pay';
      if (/google/.test(s)) return 'Google Pay';
      if (/paypal/.test(s)) return 'PayPal';
      if (/shop[\s-_]?pay|shopify-pay/.test(s)) return 'Shop Pay';
    }
    return 'Express';
  }

  // Shopify's wallet buttons live in shadow DOM; composedPath() still tells us
  // the tap landed inside the drawer's express block. pointerdown fires before
  // the wallet sheet takes over, and we never preventDefault, so the payment
  // flow is untouched.
  var last = { sig: '', at: 0 };
  document.addEventListener('pointerdown', function (e) {
    var path = e.composedPath ? e.composedPath() : [];
    var inExpress = path.some(function (el) {
      return el && el.classList && el.classList.contains('e5cd__express-buttons');
    });
    if (!inExpress || !cart || !cart.item_count) return;
    var sig = cart.token + '|' + cart.total_price;
    if (sig === last.sig && Date.now() - last.at < DEDUPE_MS) return;
    last = { sig: sig, at: Date.now() };
    track(EVENT, payload(cart, walletFrom(path)));
  }, true);

  // Cart restore: /?e5cart=vid:qty[:plan],vid:qty[:plan]
  function restore() {
    var params = new URLSearchParams(window.location.search);
    var raw = params.get('e5cart');
    if (!raw) return false;
    params.delete('e5cart');
    var clean = window.location.pathname + (params.toString() ? '?' + params : '') + window.location.hash;
    history.replaceState(null, '', clean);

    var items = raw.split(',').map(function (part) {
      var b = part.split(':');
      var item = { id: Number(b[0]), quantity: Number(b[1]) || 1 };
      if (b[2]) item.selling_plan = Number(b[2]);
      return item;
    }).filter(function (i) { return i.id; });
    if (!items.length) return false;

    nativeFetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
      .then(function () {
        return nativeFetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(loadCart)
      .then(function () {
        var d = document.querySelector('cart-drawer');
        if (!d) return;
        var go = function () {
          if (typeof d.refresh === 'function') Promise.resolve(d.refresh()).then(function () { d.open && d.open(); });
          else if (d.open) d.open();
        };
        customElements.whenDefined('cart-drawer').then(go);
      })
      .catch(function () { window.location.href = '/cart'; });
    return true;
  }

  if (!restore()) loadCart();
})();
